import React, { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  Box,
  Checkbox,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  SearchInput,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import {
  BuildingsIcon,
  InactiveIcon,
  TestIcon,
} from '@fortress-technology-solutions/fortress-component-library/Icons';
import { sortStrings } from '@fortress-technology-solutions/fortress-component-library/utils';
import { MenuButton } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import messages from './messages';
import { useFlags } from 'launchdarkly-react-client-sdk';

const PropertySearchMenu = ({
  selectedProperty,
  onSelectProperty,
  onSelectPortfolioSummary,
  userProperties,
}) => {
  const [includeInactive, setIncludeInactive] = useState(false);
  const [searchText, setSearchText] = useState('');
  const { propertyListShortName } = useFlags();

  const options = useMemo(() => {
    let filteredProperties = [];
    if (includeInactive) {
      filteredProperties = userProperties;
    } else {
      filteredProperties = userProperties.filter(
        (property) => property?.propertyStatus?.description !== 'Inactive',
      );
    }

    const trainingProperties = [];
    const assignedProperties = [];

    filteredProperties.forEach((property) => {
      const isInactive = property?.propertyStatus?.description === 'Inactive';
      const isTraining = property.isTestProperty === true;

      const displayText =
        propertyListShortName && property?.shortName
          ? `${property.name} (${property.shortName})`
          : property.name;

      const propertyOption = {
        value: property.name,
        text: displayText,
        icon: isTraining ? (
          <TestIcon color={isInactive ? 'warning' : undefined} />
        ) : (
          <BuildingsIcon color={isInactive ? 'warning' : undefined} />
        ),
        onClick: onSelectProperty(property),
        secondaryAction: isInactive ? <InactiveIcon color={'warning'} /> : null,
        isTraining,
      };

      if (isTraining) {
        trainingProperties.push(propertyOption);
      } else {
        assignedProperties.push(propertyOption);
      }
    });

    const sortedTraining = trainingProperties.sort(sortStrings('text', 'ASC'));
    const sortedAssigned = assignedProperties.sort(sortStrings('text', 'ASC'));

    return [...sortedTraining, ...sortedAssigned];
  }, [includeInactive, onSelectProperty, userProperties]);

  const trainingFiltered = useMemo(() => {
    return options
      .filter((opt) => opt.isTraining)
      .filter((option) =>
        option.text.toLowerCase().includes(searchText.toLowerCase()),
      );
  }, [options, searchText]);

  const assignedFiltered = useMemo(() => {
    return options
      .filter((opt) => !opt.isTraining)
      .filter((option) =>
        option.text.toLowerCase().includes(searchText.toLowerCase()),
      );
  }, [options, searchText]);

  const handlePropertyClick = (propertyOnClick) => (e) => {
    e.stopPropagation();
    if (propertyOnClick) {
      propertyOnClick(e);
    }
    setTimeout(() => {
      document.getElementById('property-search-menu-button')?.click();
    }, 100);
  };

  return (
    <MenuButton
      id={'property-search-menu'}
      ButtonProps={{ sx: { color: 'white' } }}
      MenuProps={{
        closeOnClick: true,
        disablePortal: true,
        keepMounted: false,
        sx: {
          '& .MuiPaper-root': {
            width: { xs: 'calc(100vw - 20px)', sm: 400 },
          },
          '& .MuiListItemButton-root': {
            margin: 0,
            borderRadius: 0,
          },
          '& .MuiListItemText-root': {
            minWidth: 0,
            overflow: 'hidden',
          },
          '& .MuiListItemSecondaryAction-root': {
            right: 8,
            pointerEvents: 'none',
          },
          '& .MuiTypography-root': {
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },
        },
      }}
      variant={'text'}
      value={selectedProperty?.name}
      placeholder={selectedProperty?.name}
      options={[]}
      menuHeader={
        <Box sx={{ width: '100%', minWidth: { sm: '100%' }, paddingX: 1 }}>
          <Box sx={{ paddingX: 1 }}>
            <ListItemButton
              id={'portfolio-summary'}
              onClick={() => {
                onSelectPortfolioSummary();
                // click outside menu to close
                document.getElementById('property-search-menu-button').click();
              }}
            >
              <ListItemIcon>
                <BuildingsIcon />
              </ListItemIcon>
              <ListItemText
                primary={<FormattedMessage {...messages.portfolioSummary} />}
              />
            </ListItemButton>
            <SearchInput
              name={'property-search'}
              isSubmitted={searchText.length > 0}
              sx={{ marginTop: 1, marginBottom: 1 }}
              value={searchText}
              onClearClick={() => setSearchText('')}
              inputProps={{
                onKeyDown: (e) => {
                  e.stopPropagation(); // prevents <ul> focus when pressing "p" ???
                },
              }}
              onChange={(e) => setSearchText(e.target.value)}
              HelperTextProps={{ sx: { display: 'none' } }}
            />
            <ListItemButton
              onClick={() => setIncludeInactive(!includeInactive)}
            >
              <ListItemIcon>
                <Checkbox checked={includeInactive} />
              </ListItemIcon>
              <ListItemText
                primary={<FormattedMessage {...messages.includeInactive} />}
              />
              <ListItemSecondaryAction>
                <InactiveIcon color={includeInactive ? 'warning' : undefined} />
              </ListItemSecondaryAction>
            </ListItemButton>
            <Divider />
          </Box>
          <Box
            sx={{
              maxHeight: '500px',
              overflowY: 'auto',
              overflowX: 'hidden',
            }}
          >
            {trainingFiltered.length > 0 && (
              <Box sx={{ my: 2 }}>
                <Box
                  sx={{
                    px: 1,
                    py: 0,
                    minHeight: '20px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant="bodyMedium">
                    <FormattedMessage {...messages.trainingProperties} />
                  </Typography>
                </Box>
                <Box sx={{ height: '8px' }} />
                {trainingFiltered.map((property) => (
                  <ListItemButton
                    key={property.value}
                    onClick={handlePropertyClick(property.onClick)}
                    sx={{
                      px: 1,
                      py: 0.25,
                      pr: 4,
                      minHeight: '32px',
                      minWidth: 0,
                      '& .MuiListItemIcon-root': { minWidth: '36px' },
                    }}
                    title={property.text}
                  >
                    <ListItemIcon>{property.icon}</ListItemIcon>
                    <ListItemText
                      primary={property.text}
                      primaryTypographyProps={{
                        sx: {
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        },
                      }}
                    />
                    {property.secondaryAction && (
                      <ListItemSecondaryAction>
                        {property.secondaryAction}
                      </ListItemSecondaryAction>
                    )}
                  </ListItemButton>
                ))}
              </Box>
            )}

            {assignedFiltered.length > 0 && (
              <Box sx={{ my: 2 }}>
                <Divider sx={{ mb: 2 }} />
                <Box
                  sx={{
                    px: 1,
                    py: 0,
                    minHeight: '20px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant="bodyMedium">
                    <FormattedMessage {...messages.assignedProperties} />
                  </Typography>
                </Box>
                <Box sx={{ height: '8px' }} />
                {assignedFiltered.map((property) => (
                  <ListItemButton
                    key={property.value}
                    onClick={handlePropertyClick(property.onClick)}
                    sx={{
                      px: 1,
                      py: 0,
                      pr: 4,
                      minHeight: '20px',
                      minWidth: 0,
                      display: 'flex',
                      alignItems: 'center',
                      '& .MuiListItemIcon-root': { minWidth: '36px' },
                    }}
                    title={property.text}
                  >
                    <ListItemIcon>{property.icon}</ListItemIcon>
                    <ListItemText
                      primary={property.text}
                      primaryTypographyProps={{
                        sx: {
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        },
                      }}
                    />
                    {property.secondaryAction && (
                      <ListItemSecondaryAction>
                        {property.secondaryAction}
                      </ListItemSecondaryAction>
                    )}
                  </ListItemButton>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      }
    >
      {!selectedProperty?.name ? (
        <FormattedMessage {...messages.portfolioSummary} />
      ) : null}
    </MenuButton>
  );
};

export default PropertySearchMenu;
